import {
  createSlice,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import http from "../../utils/axios";

export const fetchBanners = createAsyncThunk("banners/fetchBanners", async () => {
  const res = await http.get("/dashboard/banners");
  return res;
});

export const createBanner = createAsyncThunk(
  "banners/createBanner",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await http.post("/dashboard/banners", payload);
      return res;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to create banner");
    }
  }
);

export const updateBannerStatus = createAsyncThunk(
  "banners/updateBannerStatus",
  async ({ uuid, status }, { rejectWithValue }) => {
    try {
      const res = await http.post(`/dashboard/banners/${uuid}/toggle-status`, { status });
      return res;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to update banner status");
    }
  }
);

export const fetchBannerById = createAsyncThunk(
  "banners/fetchBannerById",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.get(`/dashboard/banners/${uuid}`);
      return res;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to fetch banner details");
    }
  }
);

export const updateBanner = createAsyncThunk(
  "banners/updateBanner",
  async ({ uuid, data }, { rejectWithValue }) => {
    try {
      const res = await http.post(`/dashboard/banners/${uuid}`, data);
      return res;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to update banner");
    }
  }
);

const bannersSlice = createSlice({
  name: "banner",
  initialState: {
    banners: [],
    pagination: null,
    banner: null,
    bannerCreateStatus: "idle",
    bannerChangeStatus: "idle",
    bannerFetchStatus: "idle",
    bannerUpdateStatus: "idle",
    loading: false,
    error: null,
  },
  reducers: {
    resetBannerStatuses(state) {
      state.bannerCreateStatus = "idle";
      state.bannerUpdateStatus = "idle";
      state.bannerFetchStatus = "idle";
      state.bannerChangeStatus = "idle";
      state.banner = null;
    },
    resetBannerChangeStatus(state) {
      state.bannerChangeStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      // ── fetchBanners ───────────────────────────────────────────────────────
      .addCase(fetchBanners.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBanners.fulfilled, (state, action) => {
        state.loading = false;
        state.banners = action.payload?.data || [];
        state.pagination = action.payload?.pagination || null;
      })
      .addCase(fetchBanners.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // ── updateBannerStatus ─────────────────────────────────────────────────
      .addCase(updateBannerStatus.pending, (state) => {
        state.bannerChangeStatus = "loading";
      })
      .addCase(updateBannerStatus.fulfilled, (state, action) => {
        state.bannerChangeStatus = "success";
        const { uuid, status } = action.meta.arg;
        const index = state.banners.findIndex((b) => b.id === uuid);
        if (index !== -1) {
          state.banners[index].is_enabled = status === "enable" ? 1 : 0;
        }
      })
      .addCase(updateBannerStatus.rejected, (state) => {
        state.bannerChangeStatus = "failed";
      })

      // ── createBanner ───────────────────────────────────────────────────────
      .addCase(createBanner.pending, (state) => {
        state.loading = true;
        state.bannerCreateStatus = "loading";
      })
      .addCase(createBanner.fulfilled, (state) => {
        state.loading = false;
        state.bannerCreateStatus = "success";
        state.banner = null;
      })
      .addCase(createBanner.rejected, (state) => {
        state.loading = false;
        state.banner = null;
        state.bannerCreateStatus = "failed";
      })

      // ── fetchBannerById ────────────────────────────────────────────────────
      .addCase(fetchBannerById.pending, (state) => {
        state.loading = true;
        state.bannerFetchStatus = "loading";
      })
      .addCase(fetchBannerById.fulfilled, (state, action) => {
        state.loading = false;
        state.bannerFetchStatus = "success";
        state.banner = action.payload.data;
      })
      .addCase(fetchBannerById.rejected, (state) => {
        state.loading = false;
        state.bannerFetchStatus = "failed";
        state.banner = null;
      })

      // ── updateBanner ───────────────────────────────────────────────────────
      .addCase(updateBanner.pending, (state) => {
        state.loading = true;
        state.bannerUpdateStatus = "loading";
      })
      .addCase(updateBanner.fulfilled, (state, action) => {
        state.loading = false;
        state.bannerUpdateStatus = "success";
        const index = state.banners.findIndex(
          (b) => b.id === action.meta.arg.uuid
        );
        if (index !== -1) {
          state.banners[index] = { ...state.banners[index], ...action.payload.data };
        }
        state.banner = action.payload.data;
      })
      .addCase(updateBanner.rejected, (state) => {
        state.loading = false;
        state.bannerUpdateStatus = "failed";
      });
  },
});

export const { resetBannerStatuses, resetBannerChangeStatus } = bannersSlice.actions;
export default bannersSlice.reducer;